import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { bankStatementApi } from '@/services/api';
import { BankStatement } from '@/types/client';
import { useClientApi } from '@/hooks/useClientApi';
import { formatDate, formatDateTime, formatNumber } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { LastWorkingDateFilter, loadClientOptions } from '@/components/utils/filters';
import { FilterAsyncSelect, FilterOption } from '@/components/ui';
import { useTableState } from '@/hooks/useTableState';

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.message || fallback;
  }
  return fallback;
};

const BankStatementList: React.FC = () => {
  const { getClientFilter } = useClientApi();

  const [bankStatements, setBankStatements] = useState<BankStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedClient, setSelectedClient] = useState<FilterOption | null>(null);

  const {
    currentPage, pageSize, ordering, searchQuery, debouncedSearch,
    setTotalCount, onSort, onSearchChange, onPageChange, resetPage,
    totalCount, totalPages, sortField, sortDir,
  } = useTableState({ defaultSortField: 'account_number', searchDebounceMs: 400 });

  const filterParams = useMemo(() => {
    const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
    return {
      statement_date: selectedDate,
      ordering,
      ...getClientFilter(clientVal),
      search: debouncedSearch || undefined,
    };
  }, [selectedDate, ordering, selectedClient, debouncedSearch, getClientFilter]);

  const load = useCallback(async () => {
    if (!filterParams.statement_date) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bankStatementApi.getBankStatements({
        ...filterParams,
        page: currentPage,
        page_size: pageSize,
      });
      setBankStatements(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load bank statements'));
      console.error('Error loading bank statements:', err);
    } finally {
      setLoading(false);
    }
  }, [filterParams, currentPage, pageSize, setTotalCount]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo((): ColumnDef<BankStatement>[] => [
    { key: 'account_number', header: 'Account Number', sortable: true, render: r => r.account_number },
    { key: 'client_name', header: 'Client Name', render: r => r.client_full_name || r.client_name },
    { key: 'currency', header: 'Currency', render: r => r.currency },
    { key: 'net_balance', header: 'Net Balance', align: 'right', render: r => formatNumber(r.net_balance) },
    { key: 'available_balance', header: 'Available Balance', align: 'right', render: r => formatNumber(r.available_balance) },
    { key: 'statement_date', header: 'Statement Date', sortable: true, render: r => formatDate(r.statement_date) },
    { key: 'created_ts', header: 'Uploaded At', sortable: true, render: r => formatDateTime(r.created_ts) },
  ], []);

  const hasActiveFilters = !!(selectedClient || searchQuery.trim());

  const handleClearFilters = () => {
    setSelectedClient(null);
    onSearchChange('');
    resetPage();
  };

  return (
    <DataTable
      title="Bank Statements"
      subtitle="Bank balances filed per client for the selected statement date."
      data={bankStatements}
      columns={columns}
      rowKey={r => r.id}
      totalCount={totalCount}
      loading={loading}
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={onPageChange}
      pageSize={pageSize}
      sortField={sortField}
      sortDir={sortDir}
      onSort={onSort}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by account number, client code, name…"
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      emptyMessage={`No bank statements found for ${selectedDate ? formatDate(selectedDate) : 'the selected date'}.`}
      filterSlot={
        <>
          <LastWorkingDateFilter
            label="Statement Date"
            inputId="statement-date"
            value={selectedDate}
            onChange={date => { setSelectedDate(date); resetPage(); }}
          />

          <FilterAsyncSelect
            label="Client"
            value={selectedClient}
            onChange={opt => { setSelectedClient(opt); resetPage(); }}
            loadOptions={loadClientOptions}
            placeholder="All Clients"
          />
        </>
      }
    />
  );
};

export default BankStatementList;